import { State } from "../index";
import { FormStatusTypes, formStatus } from "../../records/Form";
import { ElementTypes } from "lib/api/types";
import produce from "immer";

/**
 * Get the record for an entire form
 */
export const getForm = (state: State, props: { fid: string }) =>
	state.forms[props.fid];
/**
 * Check if a form status is set to ready
 */
export const isReady = (state: State, fid: string) =>
	getStatus(state, { fid }) === formStatus.ready;

/**
 * Get a forms status
 */
export const getStatus = (
	state: State,
	props: { fid: string }
): FormStatusTypes => getForm(state, props)?._ui.status;

/**
 * Get the root element for the form, used as the first point for rendering
 */
export const getRootElement = (state: State, props: { fid: string }) =>
	getForm(state, props)?.server.rootElement;

/**
 * Get the form label
 */
export const getLabel = (state: State, props: { fid: string }) =>
	getForm(state, props)?.server.formLabel;

/**
 * Get the entire data map for the form
 */
export const getData = (state: State, props: { fid: string }) =>
	getForm(state, props)?.server.data;

/**
 * Get the form location
 */
export const getFormLocation = (state: State, props: { fid: string }) => {
	const data = getForm(state, props)?.server.data;
	return data !== undefined && data !== null
		? Object.keys(data)[0]
		: undefined;
};

/**
 * Get form data for a specific binding
 */
export const getDataFor = (
	state: State,
	props: { fid: string; binding: string }
) => {
	const data = getData(state, props);
	if (data && data[props.binding]) return data[props.binding];
};

// Get the message held against a form (an error message)
export const getMessage = (state: State, props: { fid: string }) =>
	getForm(state, props)?._ui.message;

/**
 * Patched data is the form data with the users modifications applied
 */
export const getPatchedData = (state: State, props: { fid: string }) => {
	const data = state.forms[props.fid].server.data;
	const patches = state.forms[props.fid]._ui.patches;
	return produce(data, draft => {
		patches.forEach(patch => {
			// If there is no binding then we are probably adding a value
			// to a view that currently has no data, we use the vid as a temporary
			// namespace to store data until it is sync'd to the backend
			const binding =
				state.forms[props.fid].server.bindings[patch.vid] || patch.vid;

			if (patch.op === "add" || patch.op === "replace") {
				if (patch.data) {
					// If we don't have a binding - create a temporary one
					if (!draft[binding]) draft[binding] = [];
					if (!draft[binding][patch.rowIndex])
						draft[binding][patch.rowIndex] = {};

					draft[binding][patch.rowIndex][patch.id] = {
						...draft[binding][patch.rowIndex][patch.id],
						...patch.data,
						id: patch.id,
					};
				}
			} else {
				// We soft delete and leave it to element selectors to address
				// updating indexes and removing the data from the UI
				try {
					// draft[binding]?.[patch.rowIndex]?.[patch.id]
					draft[binding][patch.rowIndex][patch.id] = {
						...draft[binding][patch.rowIndex][patch.id],
						_deleted: true,
					};
				} catch (err) {
					console.warn("Could not apply remove patch to data", err);
				}
			}
		});
	});
};

/**
 * Returns the number of rows for a particular data entity
 * @param state redux store
 * @param fid form id
 * @param binding the key of the data entity
 */
export const getRowCount = (state: State, fid: string, binding: string) =>
	getForm(state, { fid }).server.data[binding]?.length;

/**
 * Creates a payload object for patching form updates to the API
 * @param state redux store
 * @param fid form id
 * @param keys an array of keys for elements to be included in the returned payload
 */
// export const getPayload = (
// 	state: State,
// 	fid: string,
// 	keys: Form["_ui"]["updateQueue"]
// ) => {
// 	const payload: { id: string; data: JsonPatch[]; index: number }[] = [];
// 	const data = getForm(state, { fid })._ui.data;

// 	keys.forEach(key => {
// 		const item = data[key];
// 		const payloadData = [];

// 		// Check if the value is being patched to a specific index of an array
// 		if (/^(.+\/(?:[0-9]+|-))$/.test(item.id)) {
// 			const split = jsonpointer.parse(item.id);

// 			// NOTE: We only support one level deep
// 			const parentId = `/${split[0]}`;

// 			// Get the array
// 			const parent = getOrigElementData(state, {
// 				fid,
// 				vid: item.vid,
// 				id: parentId,
// 				rowIndex: item.rowIndex,
// 			});

// 			// If the array doesn't exist create it
// 			// ?? Check if this is still needed - API may have been updated to fix this
// 			if (!parent || !parent.value) {
// 				payloadData.push(
// 					createJsonPatch(parentId, item.uuid, undefined, [])
// 				);
// 			}
// 		}

// 		// Create the json patch
// 		payloadData.push(
// 			createJsonPatch(item.id, item.uuid, item.currentValue, item.value)
// 		);

// 		payload.push({
// 			id: item.vid,
// 			data: payloadData,
// 			index: item.rowIndex,
// 		});
// 	});

// 	return payload;
// };

/**
 * Get the properties for a root view aka - form instance
 * @param state redux store
 * @param fid form id
 */
export const getFormProps = (state: State, fid: string) => {
	const rootElement = getRootElement(state, { fid });

	return {
		rootElement: rootElement,
		status: getStatus(state, { fid }),
		rootElementType: "rootView" as ElementTypes,
		label: getLabel(state, { fid }),
		dirty: isDirty(state, { fid }),
		message: getMessage(state, { fid }),
		formLocation: getFormLocation(state, { fid }),
	};
};

export const getPatches = (state: State, fid: string) => {
	return state.forms[fid]._ui.patches;
};

const isDirty = (state: State, props: { fid: string }) =>
	getForm(state, props)?.server.dirty;

export const getFormLayout = (state: State, fid: string) => {
	const data = getForm(state, { fid });
	// If check for old forms
	if (
		data?.server.hasOwnProperty("formLayout") &&
		data?.server.formLayout !== undefined
	)
		return data?.server.formLayout;
	else {
		return { fullWidth: false, maxWidth: null };
	}
};
